import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLang } from "../i18n";
import { Icon } from "./Icon";
import type { IconName } from "./icons";

export interface MenuItem { label: string; icon?: IconName; danger?: boolean; disabled?: boolean; tag?: string; title?: string; sep?: boolean; run?: () => void }

/**
 * The prototype's row options menu (⋮), positioned next to its button and closed on outside click, Escape or
 * scroll. It is drawn in document.body, so focusing it never scrolls a wide table (which would close it).
 */
export function RowMenu({ items, label }: { items: MenuItem[]; label?: string }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  useLayoutEffect(() => {
    if (!open || !btn.current || !menu.current) return;
    const r = btn.current.getBoundingClientRect(), w = menu.current.offsetWidth, h = menu.current.offsetHeight;
    let left = lang === "ar" ? r.left : r.right - w;
    left = Math.max(8, Math.min(left, innerWidth - w - 8));
    let top = r.bottom + 4;
    if (top + h > innerHeight - 8) top = Math.max(8, r.top - h - 4);
    setPos({ left, top });
    menu.current.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus({ preventScroll: true });
  }, [open, lang]);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e instanceof MouseEvent && (menu.current?.contains(e.target as Node) || btn.current?.contains(e.target as Node))) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); window.removeEventListener("scroll", close, true); };
  }, [open]);
  return (
    <>
      <button ref={btn} className="btn btn-sm btn-ghost btn-icon" aria-label={label ?? t("options")} title={t("options")} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}><Icon name="dots" /></button>
      {open && createPortal(
        <div ref={menu} className="xmenu" role="menu" style={{ left: pos.left, top: pos.top }}>
          {items.map((x, i) => x.sep ? <hr key={i} /> : (
            <button key={i} role="menuitem" className={`xm-item ${x.danger ? "danger" : ""}`} disabled={x.disabled} title={x.title} onClick={() => { setOpen(false); x.run?.(); }}>
              {x.icon && <Icon name={x.icon} />}<span>{x.label}</span>{x.tag && <span className="sim-tag">{x.tag}</span>}
            </button>
          ))}
        </div>, document.body
      )}
    </>
  );
}
